'use client';

import { cn } from '@/lib/utils';

import { BlockComponentModel } from '@/cms/types/blok-component';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { BlockHeader, BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import NextImage from '@/components/atoms/Image';
import {
  ImageStrapiProps,
  transformCMSAssetToNextImageProps,
} from '@/components/atoms/Image/ImageStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/components/atoms/Text';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/components/atoms/Toggle/useCMSToggle';
import { SimpleCard, SimpleCardProps } from '.';

export interface SimpleCardsStrapiProps {
  header?: BlockHeader;
  alignCardsContent: SimpleCardProps['align'];
  cardsVariant: 'large' | 'small';
  layout?: 'one-column' | 'two-columns' | 'two-columns-reverse';
  belowText?: RichTextFieldStrapiProps;
  cards: {
    top?: ImageStrapiProps;
    title?: string;
    description?: RichTextFieldStrapiProps;
    url?: string;
  }[];
  toggle?: ToggleCMSProps;
}

export default function SimpleCardsStrapi({
  block,
  ...rest
}: BlockComponentModel<SimpleCardsStrapiProps>) {
  const {
    cards,
    alignCardsContent = 'center',
    header,
    cardsVariant = 'small',
    layout = 'one-column',
    belowText,
    toggle,
  } = block;
  const { filteredToggleList, toggleNode: ToggleNode } = useCMSToggle<
    typeof cards
  >(cards, toggle);

  const variantsClasses = {
    large: {
      title: 'text-gray-950 text-subtitle-l-d',
      description: 'mt-6 text-gray-500 dark:text-gray-500',
    },
    small: {
      title: 'text-wrap text-gray-500',
      description: 'mt-4 text-subtitle-l-d dark:text-gray-500',
    },
  } as const;

  const layoutGapClasses = 'gap-x-6 md:gap-x-6';
  const layoutClasses = {
    'one-column': 'md:flex-col',
    'two-columns': cn('md:flex-row md:items-start', layoutGapClasses),
    'two-columns-reverse': cn(
      'md:flex-row-reverse md:items-start',
      layoutGapClasses
    ),
  } as const;

  const topChildrenToggle =
    layout === 'one-column' && ToggleNode && ToggleNode();

  const topChildrenBelowText = layout !== 'one-column' && belowText && (
    <div className='text-gray-500' data-tracker-component='additional-info'>
      <RichTextFieldStrapi block={belowText} />
    </div>
  );

  function isCardInFilteredList(cardItem: SimpleCardsStrapiProps['cards'][0]) {
    return (
      !!filteredToggleList?.length &&
      !filteredToggleList.some(
        (filteredItem) => filteredItem.title === cardItem.title
      )
    );
  }

  return (
    <BlockWrapper {...rest} data-tracker-section={SECTION_NAMES.SIMPLE_CARDS}>
      <div>
        <BlockHeaderContent
          {...header}
          alignment='center'
          classNames={{
            root: cn(
              layoutClasses?.[layout] || layoutClasses['one-column'],
              'self-stretch md:px-10'
            ),
            children: cn(
              'justify-normal',
              cardsVariant === 'small' &&
                'overflow-auto flex-nowrap md:flex-wrap flex md:grid md:grid-cols-3',
              cardsVariant === 'large' && 'grid md:grid-cols-3',
              layout === 'one-column' && 'md:flex md:justify-center'
            ),
            header: cn(
              layout !== 'one-column' &&
                'md:mb-0 lg:mb-0 md:items-start md:text-left'
            ),
            button: cn(layout !== 'one-column' && 'md:justify-start'),
          }}
          buttonPosition={
            layout !== 'one-column' ? 'top' : header?.buttonPosition
          }
          bottomChildren={
            layout === 'one-column' && belowText ? (
              <div
                className='mt-8 text-gray-500 md:mt-9'
                data-tracker-component='additional-info'
              >
                <RichTextFieldStrapi block={belowText} />
              </div>
            ) : null
          }
          topChildren={topChildrenToggle || topChildrenBelowText}
        >
          {cards.map((card, index) => (
            <div
              key={card.title || card.url || index}
              className={cn(
                'self-stretch max-w-full sm:max-w-[324px]',
                layout === 'one-column' && 'md:max-w-[324px] md:flex-1',
                isCardInFilteredList(card) && 'hide-content'
              )}
            >
              <SimpleCard
                key={index}
                align={alignCardsContent}
                url={card?.url}
                classNames={{
                  root: cn(
                    'h-full max-w-full sm:max-w-[324px]',
                    cardsVariant === 'large' && 'md:w-[324px] md:max-w-full',
                    cardsVariant === 'small' && 'min-w-[148px] md:min-w-[auto]',
                    layout === 'one-column' && 'md:flex-1'
                  ),
                }}
              >
                {card.top && (
                  <NextImage
                    {...transformCMSAssetToNextImageProps(card.top)}
                    classNames={{
                      container: 'mb-4',
                      image: cn(cardsVariant === 'large' && 'w-9'),
                    }}
                  />
                )}
                {card.title && (
                  <Text
                    as='h3'
                    classNames={{
                      wrapper: cn(variantsClasses[cardsVariant].title),
                    }}
                  >
                    {card.title}
                  </Text>
                )}
                {!!card.description?.text?.length && (
                  <div
                    className={cn(
                      'h-full',
                      variantsClasses[cardsVariant].description
                    )}
                  >
                    <RichTextFieldStrapi block={card.description} />
                  </div>
                )}
              </SimpleCard>
            </div>
          ))}
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
